'use client';

import { useTranslation } from '@/lib';
import { Badge } from '@/types/badges';
import { BadgeIcon } from './BadgeIcon';

interface BadgeCardProps {
  badge: Badge;
  unlocked?: boolean;
  unlockedAt?: string;
  progress?: number;
  onPin?: () => void;
  isPinned?: boolean;
  showPinButton?: boolean;
}

export function BadgeCard({
  badge,
  unlocked = false,
  unlockedAt,
  progress,
  onPin,
  isPinned,
  showPinButton = true
}: BadgeCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white  rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
      <div className="flex items-start gap-4">
        <BadgeIcon badge={badge} size="lg" unlocked={unlocked} />

        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className={`font-bold ${unlocked ? 'text-gray-900 ' : 'text-gray-500 '}`}>
                {t(`badges.${badge.code}.name`)}
              </h3>
              <p className="text-sm text-gray-600  mt-1">
                {t(`badges.${badge.code}.description`)}
              </p>
            </div>

            <span className={`
              px-2 py-1 rounded text-xs font-medium shrink-0 ml-2
              ${badge.rarity === 'common' && 'bg-gray-200 text-gray-700  '}
              ${badge.rarity === 'rare' && 'bg-blue-200 text-blue-700  '}
              ${badge.rarity === 'epic' && 'bg-purple-200 text-purple-700  '}
              ${badge.rarity === 'legendary' && 'bg-yellow-200 text-yellow-700  '}
            `}>
              {t(`badges.rarity.${badge.rarity}`)}
            </span>
          </div>

          {!unlocked && progress !== undefined && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-600  mb-1">
                <span>{t('badges.progress')}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-gray-200  rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
            </div>
          )}

          {unlocked && (
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs text-gray-500 ">
                🎉 {t('badges.unlocked_on')} {new Date(unlockedAt!).toLocaleDateString()}
              </span>

              {showPinButton && onPin && (
                <button
                  onClick={onPin}
                  className={`
                    text-xs px-2 py-1 rounded transition-colors
                    ${isPinned
                      ? 'bg-yellow-100 text-yellow-700  '
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200   '
                    }
                  `}
                >
                  {isPinned ? '📌 ' + t('badges.pinned') : t('badges.pin')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
